export const FRUIT_PRODUCT_IDS = [
  "APPLE",
  "GRAPE",
  "KOREAN_MELON",
  "TANGERINE",
  "STRAWBERRY",
  "WATERMELON",
] as const;

export type FruitProductId = (typeof FRUIT_PRODUCT_IDS)[number];

export const FRUIT_PRICE_WON_VALUES = [1000, 2000, 3000, 4000, 5000] as const;

export type FruitPriceWon = (typeof FRUIT_PRICE_WON_VALUES)[number];

export const OFFICIAL_PLAYER_COUNT = 13;
export const DEFAULT_ROUND_COUNT = 4;
export const FRUIT_RIGHTS_PER_PLAYER = 2;
export const EXTENSION_PENALTY_WON = 10000;

export type FruitShopV2Phase =
  | "ITEM_WINDOW"
  | "PRICE_SUBMISSION"
  | "ROUND_REVEALED"
  | "EXTENSION_READY"
  | "COMPLETED";

export type FruitShopV2Status = "IN_PROGRESS" | "COMPLETED";

export type FruitShopV2ItemStatus = "AVAILABLE" | "USED_SECRET" | "USED_CHANGE";

export interface ValidationResult {
  readonly valid: boolean;
  readonly error?: string;
  readonly details?: Record<string, unknown>;
}

export interface InitGameOptions {
  readonly playerIds: readonly string[];
  readonly hostPlayerId: string;
  readonly gameId?: string;
  readonly seed?: number;
  readonly roundCount?: number;
}

export type FruitRightsByPlayerId = Readonly<
  Record<string, readonly FruitProductId[]>
>;

export type FruitPricesByProduct = Readonly<
  Partial<Record<FruitProductId, FruitPriceWon>>
>;

export interface FruitShopV2Player {
  readonly playerId: string;
  readonly fruitRights: readonly FruitProductId[];
  readonly totalIncomeWon: number;
  readonly itemStatus: FruitShopV2ItemStatus;
}

export interface FruitChangeRecord {
  readonly playerId: string;
  readonly fromProductId: FruitProductId;
  readonly toProductId: FruitProductId;
}

export interface FruitProductMarket {
  readonly productId: FruitProductId;
  readonly sellerCount: number;
}

export type FruitMarketByProduct = Readonly<
  Record<FruitProductId, FruitProductMarket>
>;

export interface FruitRoundResult {
  readonly productId: FruitProductId;
  readonly sellerCount: number;
  readonly finalPriceWon: FruitPriceWon;
  readonly totalRevenueWon: number;
  readonly winnerIds: readonly string[];
  readonly payoutByPlayerId: Readonly<Record<string, number>>;
  readonly hidden: boolean;
}

export interface FruitShopV2Round {
  readonly roundNumber: number;
  readonly phase: FruitShopV2Phase;
  readonly itemReadyPlayerIds: readonly string[];
  readonly priceSubmissions: Readonly<Record<string, FruitPricesByProduct>>;
  readonly secretSelections: Readonly<Record<string, FruitProductId>>;
  readonly changeRecords: readonly FruitChangeRecord[];
  readonly market: FruitMarketByProduct;
  readonly productResults: readonly FruitRoundResult[];
  readonly incomeByPlayerId: Readonly<Record<string, number>>;
  readonly confirmedPlayerIds: readonly string[];
}

export interface FruitShopV2State {
  readonly gameId: string;
  readonly status: FruitShopV2Status;
  readonly roundCount: number;
  readonly currentRound: FruitShopV2Round;
  readonly completedRounds: readonly FruitShopV2Round[];
  readonly players: readonly FruitShopV2Player[];
  readonly winnerIds: readonly string[];
  readonly lowestPlayerIds: readonly string[];
  readonly garnetAwardsByPlayerId: Readonly<Record<string, number>>;
  readonly lifeTokenAwardsByPlayerId: Readonly<Record<string, number>>;
  readonly extensionNumber: number;
  readonly hostPlayerId: string;
  readonly seed: number;
}

export interface ReadyForPriceSubmissionAction {
  readonly type: "ReadyForPriceSubmission";
  readonly playerId: string;
}

export interface SubmitPricesAction {
  readonly type: "SubmitPrices";
  readonly playerId: string;
  readonly prices: FruitPricesByProduct;
}

export interface UseSecretAction {
  readonly type: "UseSecret";
  readonly playerId: string;
  readonly productId: FruitProductId;
}

export interface UseChangeAction {
  readonly type: "UseChange";
  readonly playerId: string;
  readonly fromProductId: FruitProductId;
  readonly toProductId: FruitProductId;
}

export interface RevealRoundAction {
  readonly type: "RevealRound";
  readonly playerId: string;
}

export interface ConfirmRoundResultAction {
  readonly type: "ConfirmRoundResult";
  readonly playerId: string;
}

export interface StartExtensionRoundAction {
  readonly type: "StartExtensionRound";
  readonly playerId: string;
}

export type FruitShopV2Action =
  | ReadyForPriceSubmissionAction
  | SubmitPricesAction
  | UseSecretAction
  | UseChangeAction
  | RevealRoundAction
  | ConfirmRoundResultAction
  | StartExtensionRoundAction;

export interface MaskedFruitRoundResult {
  readonly productId: FruitProductId;
  readonly sellerCount: number;
  readonly finalPriceWon: FruitPriceWon | null;
  readonly totalRevenueWon: number | null;
  readonly winnerIds: readonly string[] | null;
  readonly payoutByPlayerId: Readonly<Record<string, number>> | null;
  readonly hidden: boolean;
}

export interface FruitShopV2ViewerPlayer {
  readonly playerId: string;
  readonly ownFruitRights: readonly FruitProductId[] | null;
  readonly totalIncomeWon: number | null;
  readonly itemStatus: FruitShopV2ItemStatus | null;
}

export interface FruitShopV2ViewerRound {
  readonly roundNumber: number;
  readonly phase: FruitShopV2Phase;
  readonly itemReadyPlayerIds: readonly string[];
  readonly market: FruitMarketByProduct;
  readonly ownPriceSubmission: FruitPricesByProduct | null;
  readonly priceSubmittedPlayerIds: readonly string[];
  readonly changeRecords: readonly FruitChangeRecord[];
  readonly productResults: readonly MaskedFruitRoundResult[];
  readonly confirmedPlayerIds: readonly string[];
}

export interface FruitShopV2ViewerState {
  readonly gameId: string;
  readonly status: FruitShopV2Status;
  readonly roundCount: number;
  readonly currentRound: FruitShopV2ViewerRound;
  readonly players: readonly FruitShopV2ViewerPlayer[];
  readonly winnerIds: readonly string[];
  readonly lowestPlayerIds: readonly string[];
  readonly garnetAwardsByPlayerId: Readonly<Record<string, number>>;
  readonly lifeTokenAwardsByPlayerId: Readonly<Record<string, number>>;
  readonly extensionNumber: number;
  readonly hostPlayerId: string;
}
